import React, { useState, useEffect } from 'react';
import axiosInstance from '@/lib/axios';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * PWA Install Prompt Component
 * Shows after the user closes the USP popup
 * Stores the beforeinstallprompt event and triggers it on user action
 */
export default function PWAInstallPrompt({ show, onClose }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if it's iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event so it can be triggered later
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstallable(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // For iOS, show instructions
      if (isIOS) {
        return; // iOS instructions are shown in the UI
      }
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setIsInstallable(false);
    onClose();
  };

  const handleDismiss = () => {
    // Store that user dismissed the prompt
    sessionStorage.setItem('pwaPromptDismissed', 'true');
    onClose();
  };

  // Don't show if already dismissed this session or not installable (except iOS)
  if (!show || sessionStorage.getItem('pwaPromptDismissed')) {
    return null;
  }

  // Don't show if not installable and not iOS
  if (!isInstallable && !isIOS) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl max-w-md w-full shadow-2xl animate-slide-up">
        {/* Header */}
        <div className="relative p-6 pb-4">
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-slate-400" />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
              <Smartphone className="h-8 w-8 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900">
                Installer l'application
              </h3>
              <p className="text-sm text-slate-500">
                Accès rapide depuis votre écran d'accueil
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-4">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Accès instantané sans navigateur
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Notifications de suivi de projet
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Expérience optimisée mobile
              </li>
            </ul>
          </div>

          {/* iOS specific instructions */}
          {isIOS && (
            <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-sm font-medium text-blue-800 mb-2">
                Sur iPhone/iPad :
              </p>
              <ol className="text-sm text-blue-700 space-y-1">
                <li>1. Appuyez sur <span className="inline-flex items-center px-1 bg-blue-100 rounded">⬆ Partager</span></li>
                <li>2. Faites défiler et appuyez sur <strong>"Sur l'écran d'accueil"</strong></li>
                <li>3. Appuyez sur <strong>"Ajouter"</strong></li>
              </ol>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 pt-2 flex gap-3">
          <Button
            variant="outline"
            onClick={handleDismiss}
            className="flex-1"
          >
            Plus tard
          </Button>
          {!isIOS ? (
            <Button
              onClick={handleInstallClick}
              className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              <Download className="h-4 w-4 mr-2" />
              Installer
            </Button>
          ) : (
            <Button
              onClick={handleDismiss}
              className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              Compris !
            </Button>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
